import { analyticsTracker } from '@winnin/analytics'

import { COUNTRIES as Countries } from 'consts'
import { PanelStore } from 'stores'
import UserStore from 'stores/UserStore'

import { EVENT_NAMES } from './analytics-tracker.consts'
import getEnvFromLocation from '../getEnvFromLocation'
import localStorageUtils from '../localStorageUtils'
import { getMacroareaFromLocation } from '../macroarea'
import INFRA_USERS_IDS from '../testUsers'

const COUNTRIES = Countries.reduce(
  (accumulator, country) => ({
    ...accumulator,
    [country.ALPHA_2]: country.NAME,
  }),
  {}
)

class Mixpanel {
  isInfra = false
  eventNames = EVENT_NAMES
  countries = COUNTRIES
  client = null

  start() {
    const envUrl = getEnvFromLocation()
    const TOKENS = {
      production: '0079fb1e0369119a39329b97d8258eb3',
      staging: '5c4262dacacb83893e0b256f9362d240',
      dev: '0fc11f045ea31174e3b6070965f60130',
    }
    const envMap = {
      production: TOKENS.production,
      staging: TOKENS.staging,
      local: TOKENS.dev,
      test: TOKENS.dev,
      development: TOKENS.dev,
    }
    const token = envMap[envUrl]
    if (!token) throw new Error(`Invalid env name for mixpanel. ${envUrl}`)

    analyticsTracker.start({
      token,
      endpoint: 'https://wt.winnin.com',
      /**
       * The company fields repeat what `userTrack` registers as super properties, under the same
       * names and rules, so events sent without the SDK — a saved Zai report reporting its own
       * openings — still say which client they belong to.
       */
      getContext: () => {
        const user = Object(UserStore.userInfo)
        const company = Object(user.company)
        return {
          workspaceId: Object(PanelStore?.selectedPanel)?.id,
          'Workspace Name': Object(PanelStore?.selectedPanel)?.name,
          companyId: company.id,
          'Company Name': user.role === 25 ? 'Guest' : company.name,
          'Company Type': company.companyType?.name,
          'Contract Insights Plan': company.plan?.name,
        }
      },
      shouldTrack: () => !UserStore.isImpersonating,
    })

    this.client = analyticsTracker
  }

  loginTrack(target, params, location: Location = undefined) {
    if (INFRA_USERS_IDS.includes(params?.id)) return

    analyticsTracker.loginTrack(target, {
      'Macro Area': getMacroareaFromLocation(location),
      ...params,
    })
  }

  eventTrack(target: string, params?: Record<string, any>, location: Location = undefined) {
    const isTestEnvironment = localStorageUtils.getObject('isTestUser')
    if (INFRA_USERS_IDS.includes(UserStore.get('id')) || isTestEnvironment) return

    analyticsTracker.track(target, {
      'Macro Area': getMacroareaFromLocation(location),
      ...params,
    })
  }

  userTrack(user) {
    const isTestEnvironment = localStorageUtils.getObject('isTestUser')
    if (INFRA_USERS_IDS.includes(user.id) || isTestEnvironment) return

    analyticsTracker.identify(user.id.toString(), {
      id: user.id,
      role: user.role,
      name: user.name,
      'User Name': user.name,
      'Company Name': user.role === 25 ? 'Guest' : user.company.name,
      companyId: user.company.id,
      'Company Type': user.company.companyType.name,
      'Job Title': user.jobTitle ?? user.jobTitleType.name,
      'Contract Insights Plan': user.company.plan.name,
      companyName: user.company.name,
      email: user.email,
      ...(user?.releaseType && { releaseType: user?.releaseType }),
      $email: user.email,
      $direct: window.location.hostname,
    })
  }
}

export default new Mixpanel()
